import { useState } from "react";
import { api, gbp, duffelLogo, airlineLogo } from "@/lib/api";
import { AirportAutocomplete } from "@/components/AirportAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, Loader2, Send, Plane, CheckCircle2 } from "lucide-react";

export const QuoteBuilder = ({ onSent }) => {
  const [origin, setOrigin] = useState(null);
  const [dest, setDest] = useState(null);
  const [cabin, setCabin] = useState("Economy");
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [sellPrice, setSellPrice] = useState("");
  const [voucher, setVoucher] = useState("");
  const [deposit, setDeposit] = useState("");
  const [sending, setSending] = useState(false);

  const search = async () => {
    if (!origin?.code || !dest?.code) { toast.error("Pick origin and destination"); return; }
    setSearching(true); setResults([]); setSelected(null);
    try {
      const { data } = await api.get("/flights/search", { params: { origin: origin.code, destination: dest.code, cabin, ...(departDate ? { depart_date: departDate } : {}), ...(returnDate ? { return_date: returnDate } : {}) } });
      setResults(data.flights || []);
      if (!data.flights?.length) toast.info("No live fares found for those dates");
    } catch { toast.error("Search failed"); } finally { setSearching(false); }
  };

  const pick = (f) => { setSelected(f); setSellPrice(String(Math.round(f.price))); };

  const send = async () => {
    if (!selected) { toast.error("Select a flight first"); return; }
    if (!clientEmail) { toast.error("Enter the client's email"); return; }
    setSending(true);
    try {
      const flight = { ...selected, depart_date: departDate, return_date: returnDate,
        passenger_name: clientName };
      const passengers = [{ first: (clientName || "").split(" ")[0] || "", last: (clientName || "").split(" ").slice(1).join(" ") }];
      for (let i = 1; i < Number(adults); i++) passengers.push({ first: "Passenger", last: String(i + 1) });
      const { data } = await api.post("/admin/quotes", {
        flight, passengers, email: clientEmail, contact_number: phone,
        sell_price: Number(sellPrice) || selected.price, voucher, deposit: Number(deposit) || 0,
      });
      toast[data.emailed ? "success" : "error"](data.emailed ? `Quote ${data.reference} sent to ${clientEmail}` : "Quote saved but email failed");
      setSelected(null); setResults([]); setClientName(""); setClientEmail(""); setPhone(""); setSellPrice(""); setVoucher(""); setDeposit("");
      onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send quote"); } finally { setSending(false); }
  };

  const live = selected ? Math.round(selected.price) : 0;
  const sell = Number(sellPrice) || 0;
  const margin = sell - live;

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Quote Builder</h1>
      <p className="text-slate-500 text-sm mb-6">Search live fares, set your own price, apply a voucher, and email a professional PDF quote to the client.</p>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 grid md:grid-cols-[1fr_1fr_auto_auto_auto_auto] gap-3 items-end mb-5">
        <div><Label className="text-xs">From</Label><AirportAutocomplete testid="qb-origin" placeholder="City / airport" value={origin ? `${origin.city} (${origin.code})` : ""} onSelect={setOrigin} /></div>
        <div><Label className="text-xs">To</Label><AirportAutocomplete testid="qb-destination" placeholder="City / airport" value={dest ? `${dest.city} (${dest.code})` : ""} onSelect={setDest} /></div>
        <div><Label className="text-xs">Depart</Label><Input data-testid="qb-depart" type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} /></div>
        <div><Label className="text-xs">Return</Label><Input data-testid="qb-return" type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} /></div>
        <div><Label className="text-xs">Cabin</Label>
          <select data-testid="qb-cabin" value={cabin} onChange={(e) => setCabin(e.target.value)} className="w-full rounded-md border border-slate-200 px-2 py-2 text-sm bg-white h-10"><option>Economy</option><option>Premium Economy</option><option>Business</option><option>First</option></select>
        </div>
        <Button data-testid="qb-search" onClick={search} disabled={searching} className="rounded-full bg-[#0B3C5D] text-white h-10">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}</Button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {results.slice(0, 8).map((f) => (
            <button key={f.id} data-testid={`qb-result-${f.id}`} onClick={() => pick(f)}
              className={`text-left bg-white rounded-xl border p-3 flex items-center gap-3 transition-all ${selected?.id === f.id ? "border-[#FF6B35] ring-2 ring-[#FF6B35]/30" : "border-slate-200 hover:border-slate-300"}`}>
              <img src={f.logo_url || duffelLogo(f.airline_code)} alt="" className="h-7 w-16 object-contain" onError={(e) => { const a = airlineLogo(f.airline_code); if (e.target.src !== a) e.target.src = a; else e.target.style.display = "none"; }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{f.airline}</p>
                <p className="text-xs text-slate-500">{f.origin_code}→{f.destination_code} · {f.depart_time} · {f.stops}</p>
              </div>
              <span className="font-display font-bold text-[#0B3C5D]">{gbp(f.price)}</span>
              {selected?.id === f.id && <CheckCircle2 className="w-4 h-4 text-[#FF6B35]" />}
            </button>
          ))}
        </div>
      )}

      {/* Quote form */}
      {selected && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="qb-form">
          <p className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><Plane className="w-4 h-4 text-[#FF6B35]" /> Prepare quote — {selected.airline} {selected.origin_code}→{selected.destination_code}</p>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Client name</Label><Input data-testid="qb-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Full name" /></div>
            <div><Label>Client email</Label><Input data-testid="qb-client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@email.com" /></div>
            <div><Label>Phone</Label><Input data-testid="qb-phone" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            <div><Label>Passengers</Label><Input data-testid="qb-adults" type="number" min="1" value={adults} onChange={(e) => setAdults(e.target.value)} /></div>
            <div><Label>Your price (per person)</Label><Input data-testid="qb-sell-price" type="number" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} /></div>
            <div><Label>Voucher code <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="qb-voucher" value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())} /></div>
            <div><Label>Deposit now <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="qb-deposit" type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            <span className="text-slate-500">Live cost: <b className="text-slate-700">{gbp(live)}</b></span>
            <span className="text-slate-500">Your price: <b className="text-[#0B3C5D]">{gbp(sell)}</b> pp</span>
            <span className={margin >= 0 ? "text-green-600" : "text-red-500"}>Margin: <b>{gbp(margin)}</b> pp</span>
          </div>
          <Button data-testid="qb-send" onClick={send} disabled={sending} className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold mt-4 px-6">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send professional quote
          </Button>
        </div>
      )}
    </div>
  );
};
